import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { Bars } from "react-loader-spinner";
import axiosInstance from "../../Shared/axiosInstance";

export default function SupplierFullFinancial() {
  const SUPPLIERS_API = "/Supplier/all";
  const FINANCIAL_SUMMARY_API = "/Supplier";

  const [suppliers, setSuppliers] = useState([]);
  const [supplierSearch, setSupplierSearch] = useState("");
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  
  const [selectedSupplierId, setSelectedSupplierId] = useState("");
  const [financialData, setFinancialData] = useState(null);
  const [loadingFinancial, setLoadingFinancial] = useState(false);

  // Fetch suppliers with search
  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        setLoadingSuppliers(true);
        const { data } = await axiosInstance.get(SUPPLIERS_API, {
          params: {
            name: supplierSearch || undefined,
            pageNumber: 1,
            pageSize: 10,
          },
        });
        setSuppliers(data.data || []);
      } catch (err) {
        console.error("Error fetching suppliers:", err);
        Swal.fire("خطأ", "فشل في تحميل الموردين", "error");
      } finally {
        setLoadingSuppliers(false);
      }
    };

    const delay = setTimeout(fetchSuppliers, 300);
    return () => clearTimeout(delay);
  }, [supplierSearch]);

  // Fetch financial summary for selected supplier
  const fetchFinancialSummary = async (supplierId) => {
    if (!supplierId) {
      Swal.fire("تنبيه", "يرجى اختيار مورد أولاً", "warning");
      return;
    }

    try {
      setLoadingFinancial(true);
      const { data } = await axiosInstance.get(
        `${FINANCIAL_SUMMARY_API}/${supplierId}/financial-summary`
      );
      setFinancialData(data.data);
    } catch (err) {
      console.error("Error fetching financial summary:", err);
      Swal.fire(
        "خطأ",
        err.response?.data?.message || "فشل في تحميل الملخص المالي",
        "error"
      );
    } finally {
      setLoadingFinancial(false);
    }
  };

  const handleSupplierChange = (supplierId) => {
    setSelectedSupplierId(supplierId);
    if (supplierId) {
      fetchFinancialSummary(supplierId);
    } else {
      setFinancialData(null);
    }
  };

  const handleReset = () => {
    setSelectedSupplierId("");
    setSupplierSearch("");
    setFinancialData(null);
  };

  // Show loading spinner during initial load
  if (loadingFinancial) {
    return (
      <div className="vh-100 d-flex align-items-center justify-content-center flex-column">
        <Bars height="80" width="80" color="#2563eb" ariaLabel="loading" />
        <p className="mt-3" style={{ color: "#64748b", fontSize: "18px" }}>
          جارٍ تحميل البيانات...
        </p>
      </div>
    );
  }

  return (
    <div className="container py-5" style={{ maxWidth: "1400px" }}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 mt-5 pt-5">
        <div>
          <h2 style={{ color: "#2563eb", fontWeight: "700", fontSize: "28px", marginBottom: "4px" }}>
            <i className="bi bi-building me-2"></i>الملخص المالي للمورد
          </h2>
          <small style={{ color: "#64748b", fontSize: "14px" }}>
            عرض شامل لجميع المعاملات المالية مع المورد
          </small>
        </div>
      </div>

      {/* Supplier Selection Section */}
      <div
        className="mb-4"
        style={{
          background: "#fff",
          border: "1px solid #dbeafe",
          borderRadius: "8px",
          padding: "20px",
          boxShadow: "0 4px 12px rgba(59, 130, 246, 0.15)",
        }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 style={{ color: "#1e40af", fontWeight: "600", fontSize: "16px", margin: 0 }}>
            <i className="bi bi-search me-2"></i>اختيار المورد
          </h5>
          {loadingFinancial && (
            <div className="d-flex align-items-center gap-2">
              <div
                className="spinner-border spinner-border-sm"
                role="status"
                style={{ color: "#2563eb", width: "18px", height: "18px" }}
              >
                <span className="visually-hidden">جارٍ التحميل...</span>
              </div>
              <small style={{ color: "#64748b", fontSize: "13px" }}>جارٍ تحميل البيانات...</small>
            </div>
          )}
        </div>

        <div className="row g-3 align-items-end">
          <div className="col-md-4">
            <label style={labelStyle}>البحث عن المورد</label>
            <input
              type="text"
              placeholder="🔍 اكتب اسم المورد..."
              value={supplierSearch}
              onChange={(e) => setSupplierSearch(e.target.value)}
              style={inputStyle}
            />
          </div>

          <div className="col-md-5">
            <label style={labelStyle}>اختر المورد</label>
            <select
              value={selectedSupplierId}
              onChange={(e) => handleSupplierChange(e.target.value)}
              style={inputStyle}
              disabled={loadingFinancial}
            >
              <option value="">-- اختر المورد --</option>
              {loadingSuppliers ? (
                <option disabled>جارٍ التحميل...</option>
              ) : suppliers.length > 0 ? (
                suppliers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} - {s.details}
                  </option>
                ))
              ) : (
                <option disabled>لم يتم العثور على موردين</option>
              )}
            </select>
          </div>

          <div className="col-md-3">
            <button
              onClick={handleReset}
              disabled={loadingFinancial}
              style={{
                ...buttonStyle("#64748b"),
                width: "100%",
                opacity: loadingFinancial ? 0.6 : 1,
                cursor: loadingFinancial ? "not-allowed" : "pointer",
              }}
            >
              <i className="bi bi-arrow-clockwise me-1"></i> إعادة الضبط
            </button>
          </div>
        </div>
      </div>

      {/* Financial Summary Display */}
      {financialData && (
        <>
          {/* Supplier Info Card */}
          <div
            className="mb-4"
            style={{
              background: "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)",
              borderRadius: "12px",
              padding: "24px",
              color: "#fff",
              boxShadow: "0 8px 24px rgba(37, 99, 235, 0.3)",
            }}
          >
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h4 style={{ fontWeight: "700", fontSize: "24px", marginBottom: "4px" }}>
                  {financialData.supplierName}
                </h4>
              
              </div>
              <div style={{ fontSize: "48px", opacity: 0.2 }}>
                <i className="bi bi-building"></i>
              </div>
            </div>
          </div>

          {/* Grand Totals - Main Cards */}
          <div className="row g-3 mb-4">
            {/* Grand Total Amount */}
            <div className="col-md-4">
              <div
                style={{
                  background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                  border: "none",
                  borderRadius: "12px",
                  padding: "28px 20px",
                  textAlign: "center",
                  boxShadow: "0 8px 24px rgba(139, 92, 246, 0.3)",
                  height: "100%",
                }}
              >
                <div style={{ color: "rgba(255, 255, 255, 0.85)", fontSize: "13px", fontWeight: "600", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "1px" }}>
                  <i className="bi bi-calculator me-2"></i>
                  الإجمالي الكلي
                </div>
                <div style={{ color: "#fff", fontSize: "36px", fontWeight: "700", marginBottom: "4px" }}>
                  {financialData.grandTotalAmount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} ج.م
                </div>
                <small style={{ color: "rgba(255, 255, 255, 0.75)", fontSize: "12px" }}>
                  المبلغ الإجمالي للمعاملات
                </small>
              </div>
            </div>

            {/* Grand Total Paid */}
            <div className="col-md-4">
              <div
                style={{
                  background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                  border: "none",
                  borderRadius: "12px",
                  padding: "28px 20px",
                  textAlign: "center",
                  boxShadow: "0 8px 24px rgba(34, 197, 94, 0.3)",
                  height: "100%",
                }}
              >
                <div style={{ color: "rgba(255, 255, 255, 0.85)", fontSize: "13px", fontWeight: "600", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "1px" }}>
                  <i className="bi bi-check-circle me-2"></i>
                  المدفوع الكلي
                </div>
                <div style={{ color: "#fff", fontSize: "36px", fontWeight: "700", marginBottom: "4px" }}>
                  {financialData.grandTotalPaid.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} ج.م
                </div>
                <small style={{ color: "rgba(255, 255, 255, 0.75)", fontSize: "12px" }}>
                  إجمالي المدفوعات المسددة
                </small>
              </div>
            </div>

            {/* Grand Total Remaining */}
            <div className="col-md-4">
              <div
                style={{
                  background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                  border: "none",
                  borderRadius: "12px",
                  padding: "28px 20px",
                  textAlign: "center",
                  boxShadow: "0 8px 24px rgba(220, 38, 38, 0.3)",
                  height: "100%",
                }}
              >
                <div style={{ color: "rgba(255, 255, 255, 0.85)", fontSize: "13px", fontWeight: "600", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "1px" }}>
                  <i className="bi bi-hourglass-split me-2"></i>
                  المتبقي الكلي
                </div>
                <div style={{ color: "#fff", fontSize: "36px", fontWeight: "700", marginBottom: "4px" }}>
                  {financialData.grandTotalRemaining.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} ج.م
                </div>
                <small style={{ color: "rgba(255, 255, 255, 0.75)", fontSize: "12px" }}>
                  المبالغ المستحقة للدفع
                </small>
              </div>
            </div>
          </div>

          {/* Detailed Breakdown Section */}
          <div className="row g-4">
            {/* Opening Balance Section */}
            <div className="col-md-6">
              <div style={sectionCardStyle}>
                <div style={sectionHeaderStyle("#3b82f6")}>
                  <i className="bi bi-calendar-check me-2"></i>
                  الرصيد الافتتاحي
                </div>
                <div className="p-3">
                  <div style={detailRowStyle}>
                    <span style={{ color: "#64748b", fontSize: "15px" }}>المبلغ الكلي:</span>
                    <span style={{ color: "#1e293b", fontSize: "18px", fontWeight: "700" }}>
                      {financialData.openingBalanceTotalAmount.toLocaleString(undefined, {minimumFractionDigits: 2})} ج.م
                    </span>
                  </div>
                  <div style={detailRowStyle}>
                    <span style={{ color: "#64748b", fontSize: "15px" }}>المدفوع:</span>
                    <span style={{ color: "#22c55e", fontSize: "18px", fontWeight: "700" }}>
                      {financialData.openingBalancePaidAmount.toLocaleString(undefined, {minimumFractionDigits: 2})} ج.م
                    </span>
                  </div>
                  <div style={detailRowStyle}>
                    <span style={{ color: "#64748b", fontSize: "15px" }}>المتبقي:</span>
                    <span style={{ color: "#dc2626", fontSize: "18px", fontWeight: "700" }}>
                      {financialData.openingBalanceRemainingAmount.toLocaleString(undefined, {minimumFractionDigits: 2})} ج.م
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Purchases Section */}
            <div className="col-md-6">
              <div style={sectionCardStyle}>
                <div style={sectionHeaderStyle("#f59e0b")}>
                  <i className="bi bi-cart-plus me-2"></i>
                  المشتريات
                </div>
                <div className="p-3">
                  <div style={detailRowStyle}>
                    <span style={{ color: "#64748b", fontSize: "15px" }}>المبلغ الكلي:</span>
                    <span style={{ color: "#1e293b", fontSize: "18px", fontWeight: "700" }}>
                      {financialData.purchasesTotalAmount.toLocaleString(undefined, {minimumFractionDigits: 2})} ج.م
                    </span>
                  </div>
                  <div style={detailRowStyle}>
                    <span style={{ color: "#64748b", fontSize: "15px" }}>المدفوع:</span>
                    <span style={{ color: "#22c55e", fontSize: "18px", fontWeight: "700" }}>
                      {financialData.purchasesPaidAmount.toLocaleString(undefined, {minimumFractionDigits: 2})} ج.م
                    </span>
                  </div>
                  <div style={detailRowStyle}>
                    <span style={{ color: "#64748b", fontSize: "15px" }}>المتبقي:</span>
                    <span style={{ color: "#dc2626", fontSize: "18px", fontWeight: "700" }}>
                      {financialData.purchasesRemainingAmount.toLocaleString(undefined, {minimumFractionDigits: 2})} ج.م
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Progress Bar */}
          <div className="mt-4" style={sectionCardStyle}>
            <div style={sectionHeaderStyle("#8b5cf6")}>
              <i className="bi bi-graph-up me-2"></i>
              نسبة السداد
            </div>
            <div className="p-4">
              <div style={{ marginBottom: "12px" }}>
                <div className="d-flex justify-content-between mb-2">
                  <span style={{ color: "#64748b", fontSize: "14px", fontWeight: "600" }}>نسبة المدفوع من الإجمالي</span>
                  <span style={{ color: "#2563eb", fontSize: "16px", fontWeight: "700" }}>
                    {((financialData.grandTotalPaid / financialData.grandTotalAmount) * 100).toFixed(1)}%
                  </span>
                </div>
                <div style={{ background: "#e0e7ff", borderRadius: "10px", height: "20px", overflow: "hidden" }}>
                  <div
                    style={{
                      background: "linear-gradient(90deg, #22c55e 0%, #16a34a 100%)",
                      height: "100%",
                      width: `${(financialData.grandTotalPaid / financialData.grandTotalAmount) * 100}%`,
                      borderRadius: "10px",
                      transition: "width 0.5s ease",
                    }}
                  ></div>
                </div>
              </div>
              <div className="row g-3 mt-2">
                <div className="col-md-6 text-center">
                  <div style={{ padding: "12px", background: "#dcfce7", borderRadius: "8px" }}>
                    <small style={{ color: "#15803d", fontSize: "12px", fontWeight: "600" }}>تم الدفع</small>
                    <div style={{ color: "#15803d", fontSize: "20px", fontWeight: "700" }}>
                      {financialData.grandTotalPaid.toLocaleString()} ج.م
                    </div>
                  </div>
                </div>
                <div className="col-md-6 text-center">
                  <div style={{ padding: "12px", background: "#fee2e2", borderRadius: "8px" }}>
                    <small style={{ color: "#991b1b", fontSize: "12px", fontWeight: "600" }}>المتبقي</small>
                    <div style={{ color: "#991b1b", fontSize: "20px", fontWeight: "700" }}>
                      {financialData.grandTotalRemaining.toLocaleString()} ج.م
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* No Data State */}
      {!financialData && (
        <div
          style={{
            background: "#fff",
            border: "2px dashed #cbd5e1",
            borderRadius: "12px",
            padding: "60px 20px",
            textAlign: "center",
          }}
        >
          <i className="bi bi-inbox" style={{ fontSize: "64px", color: "#cbd5e1", marginBottom: "16px" }}></i>
          <h5 style={{ color: "#64748b", fontWeight: "600", marginBottom: "8px" }}>
            لا توجد بيانات لعرضها
          </h5>
          <p style={{ color: "#94a3b8", fontSize: "14px" }}>
            يرجى اختيار مورد من القائمة أعلاه لعرض الملخص المالي
          </p>
        </div>
      )}
    </div>
  );
}

// Styles
const labelStyle = {
  color: "#475569",
  fontSize: "14px",
  fontWeight: "600",
  marginBottom: "6px",
  display: "block",
};

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  border: "1px solid #bfdbfe",
  borderRadius: "6px",
  fontSize: "15px",
  color: "#475569",
  outline: "none",
  background: "#fff",
};

const buttonStyle = (bgColor) => ({
  background: bgColor,
  color: "#fff",
  padding: "10px 16px",
  borderRadius: "6px",
  border: "none",
  fontWeight: "600",
  fontSize: "14px",
  cursor: "pointer",
  transition: "all 0.3s ease",
});

const sectionCardStyle = {
  background: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: "8px",
  overflow: "hidden",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
};

const sectionHeaderStyle = (bgColor) => ({
  background: bgColor,
  color: "#fff",
  padding: "12px 16px",
  fontSize: "16px",
  fontWeight: "600",
});

const detailRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "12px 0",
  borderBottom: "1px solid #f1f5f9",
};